import { cn } from "~/lib/cn";
import { IMAGE_VARIANTS, type ImageVariant } from "~/lib/images/variants";

/**
 * T219 placeholder contract: a slot with no published asset renders this
 * token-styled neutral block — never a broken <img>, never an empty alt.
 * Copy-free by design (no i18n surface); `aria-hidden` because it carries
 * no content. The variant reserves the same box the real asset would take
 * (anti-CLS); variants with free height (heroes) and videos reserve 16/9.
 */
export type AssetPlaceholderProps = {
  variant?: ImageVariant;
  className?: string;
};

export function AssetPlaceholder({ variant, className }: AssetPlaceholderProps) {
  const config = variant ? IMAGE_VARIANTS[variant] : null;
  const aspectRatio = config?.height ? `${config.width} / ${config.height}` : "16 / 9";
  return (
    <div
      aria-hidden
      className={cn("w-full", className)}
      style={{
        aspectRatio,
        background: "var(--color-surfaceMuted)",
        border: "1px solid var(--color-borderLight)",
        borderRadius: "var(--tfc-radius-lg)",
      }}
    />
  );
}

/** Dev-only visibility for empty/mismatched slots (T219 fallback contract). */
export function warnEmptyAssetSlot(slot: string, detail: string) {
  if (import.meta.env.DEV) {
    console.warn(`[page-assets] slot "${slot}": ${detail} — rendering placeholder`);
  }
}
